import { Close as ResetIcon, Search as SearchIcon } from "@mui/icons-material";
import { Divider, Grid, IconButton, Stack, TextField } from "@mui/material";
import type { FC } from "react";
import type { UserContext } from "../../auth/auth";
import CustomerSelect from "../../components/customerSelect/CustomerSelect";
import DatePicker from "../../components/datePicker/DatePickerV2";
import TagSelect from "../../components/tagSelect/TagSelect";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import { endOfNextYear, sanitizeDayjs, startOfPreviousMonth } from "../../misc/dateUtils";
import AgentSelect from "../../sharedComponents/agentSelect/AgentSelect";
import OrderStatusMultiSelect from "../orderStatus/OrderStatusMultiselect";
import type { DisplayModeCounts } from "../useFilteredOrders";
import { useTripTagsByCategory } from "../useTripTagsByCategory";
import ContactPersonSelect from "./ContactPersonSelect";
import DisplayModeFilter from "./DisplayModeFilter";
import GroupTypeMultiSelect from "./GroupTypeMultiSelect";
import "./OrdersSearch.scss";
import ShowHistoricalCheckbox from "./ShowHistoricalCheckbox";
import type { OrdersSearchCriteria } from "./useOrderSearch";

interface Props {
  readonly search: OrdersSearchCriteria;
  readonly displayModeCounts: DisplayModeCounts;
  readonly context: UserContext | undefined;
  readonly showHistorical: boolean;
  readonly onChange: (search: Partial<OrdersSearchCriteria>) => void;
  readonly onShowHistoricalChange: (showHistorical: boolean) => void;
  readonly onClear: () => void;
}

const OrdersSearch: FC<Props> = ({
  search,
  displayModeCounts,
  context,
  showHistorical,
  onChange,
  onShowHistoricalChange,
  onClear,
}) => {
  const { tripTagsByCategory } = useTripTagsByCategory();
  const isAdminOrAgent = context?.role === "ADMIN" || context?.role === "AGENT";
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, lg: 2 }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "center",
          }}
        >
          <SearchIcon />
          <TextField
            fullWidth
            value={search.text}
            label="Szukaj"
            onChange={(e) => onChange({ text: e.target.value })}
          />
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, lg: 10 }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "center",
          }}
        >
          <OrderStatusMultiSelect values={search.statuses} onChange={(statuses) => onChange({ statuses })} fullWidth />
          {isAdminOrAgent && (
            <GroupTypeMultiSelect values={search.groupTypes} onChange={(groupTypes) => onChange({ groupTypes })} />
          )}
          {isAdminOrAgent && (
            <CustomerSelect customerId={search.customerId} onChange={(customerId) => onChange({ customerId })} />
          )}
          {isAdminOrAgent && <AgentSelect agentId={search.agentId} onChange={(agentId) => onChange({ agentId })} />}
          {context?.role === "CUSTOMER" && (
            <ContactPersonSelect
              customerId={context.customerId}
              contactPersonEmail={search.contactPersonEmail}
              onChange={(contactPersonEmail) => onChange({ contactPersonEmail })}
            />
          )}
          <DatePicker
            label="Data rozpoczęcia od"
            fullWidth
            value={search.startDate}
            minDate={dayjs("2016-01-01", ISO_DATE_FORMAT)}
            maxDate={search.endDate}
            onChange={(d) => {
              const next = sanitizeDayjs(d, startOfPreviousMonth());
              if (next.isAfter(search.endDate, "day")) {
                onChange({ startDate: next, endDate: next });
              } else {
                onChange({ startDate: next });
              }
            }}
          />
          <DatePicker
            label="Data rozpoczęcia do"
            fullWidth
            value={search.endDate}
            minDate={search.startDate}
            maxDate={dayjs().add(3, "years")}
            onChange={(d) => {
              const next = sanitizeDayjs(d, endOfNextYear());
              if (next.isBefore(search.startDate, "day")) {
                onChange({ startDate: next, endDate: next });
              } else {
                onChange({ endDate: next });
              }
            }}
          />
          <TagSelect
            variant="icon"
            tags={search.tags}
            availableTags={tripTagsByCategory}
            onChange={(tags) => onChange({ tags })}
          />
          {isAdminOrAgent && (
            <DisplayModeFilter
              values={search.displayModes}
              counts={displayModeCounts}
              onChange={(displayModes) => onChange({ displayModes })}
            />
          )}
          <IconButton color="inherit" onClick={onClear} aria-label="Wyczyść filtry">
            <ResetIcon />
          </IconButton>
          <Divider orientation="vertical" flexItem />
          <ShowHistoricalCheckbox value={showHistorical} onChange={onShowHistoricalChange} />
        </Stack>
      </Grid>
    </Grid>
  );
};

export default OrdersSearch;
